import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadFile, UploadFailure, type UploadPhase, type UploadTargetRef } from '@/lib/upload';

/**
 * The upload queue behind FIL-7's drop zone.
 *
 * A hook rather than component state because two of the three rules it enforces
 * are effects, and an effect is only honest when something mounts it:
 *
 * - **One file at a time.** Files share one uplink, so running them together
 *   makes every bar finish at the same moment and none of them mean anything.
 *   Serialised, the queue can say "2 of 5" and each bar is a real estimate.
 * - **One run per file, ever.** `main.tsx` renders under `StrictMode`, so
 *   effects fire twice in development. A runner without a guard would open two
 *   sessions and send the file twice.
 * - **A retry resumes.** The session id is kept per row, so trying again picks
 *   up the blocks that already crossed rather than starting a multi-gigabyte
 *   transfer over (UPL-2).
 *
 * **Resume is bounded by the page.** A `File` handle does not survive a reload,
 * so nothing here is persisted and `active` exists so the caller can put the
 * browser's own unload warning in the way.
 */

export type QueueItemState =
  | { kind: 'waiting' }
  | { kind: 'uploading'; phase: UploadPhase; bytesSent: number }
  | { kind: 'done' }
  /** `detail` is the API's own sentence, present only when it gave one. */
  | { kind: 'failed'; permanent: boolean; detail?: string };

export interface QueueItem {
  id: string;
  name: string;
  sizeBytes: number;
  state: QueueItemState;
}

export interface UseUploadQueueResult {
  items: QueueItem[];
  add: (files: readonly File[]) => void;
  /** Resume a failed row. A no-op on a row that is not failed. */
  retry: (id: string) => void;
  /** Drop a settled row from the list. The bytes are unaffected either way. */
  dismiss: (id: string) => void;
  /** Anything queued or in flight — what the unload guard watches. */
  active: boolean;
}

export function useUploadQueue(
  target: UploadTargetRef,
  onUploaded: () => void,
): UseUploadQueueResult {
  const [items, setItems] = useState<QueueItem[]>([]);

  // The `File` objects stay out of state: a render has no business holding a
  // file handle, and keeping the rows plain keeps them cheap to re-render on
  // every progress tick.
  const files = useRef(new Map<string, File>());
  const sessions = useRef(new Map<string, string>());
  const running = useRef<string | undefined>(undefined);

  // Read at call time rather than captured, so the runner never closes over a
  // stale folder or a stale callback without re-subscribing the effect.
  //
  // Synced in an effect rather than during render — a render is not allowed to
  // touch a ref — and **declared before the runner below**, because effects fire
  // in declaration order and the runner reads what this writes.
  const latest = useRef({ target, onUploaded });
  useEffect(() => {
    latest.current = { target, onUploaded };
  });

  const patch = useCallback((id: string, state: QueueItemState) => {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, state } : item)));
  }, []);

  useEffect(() => {
    if (running.current !== undefined) return;

    const next = items.find((item) => item.state.kind === 'waiting');
    if (next === undefined) return;

    running.current = next.id;
    void run(next.id);

    async function run(id: string): Promise<void> {
      const file = files.current.get(id);
      if (file === undefined) {
        running.current = undefined;
        return;
      }

      patch(id, { kind: 'uploading', phase: 'opening', bytesSent: 0 });

      try {
        await uploadFile(file, latest.current.target, {
          // Present only on a retry, and that is what makes it a resume.
          ...(sessions.current.has(id) ? { resumeSessionId: sessions.current.get(id) ?? '' } : {}),
          onSessionOpened: (sessionId) => {
            sessions.current.set(id, sessionId);
          },
          onProgress: (progress) => {
            patch(id, {
              kind: 'uploading',
              phase: progress.phase,
              bytesSent: progress.bytesSent,
            });
          },
        });

        patch(id, { kind: 'done' });
        // Per file, not per drained queue: UPL-5 makes a file visible at
        // finalize, so each one appearing as it lands is the honest rendering.
        latest.current.onUploaded();
      } catch (error) {
        patch(id, {
          kind: 'failed',
          permanent: error instanceof UploadFailure && error.permanent,
          ...(error instanceof UploadFailure && error.detail !== undefined
            ? { detail: error.detail }
            : {}),
        });
      } finally {
        // Cleared after the state write, so the render it schedules re-runs
        // this effect with the next waiting row already visible.
        running.current = undefined;
      }
    }
  }, [items, patch]);

  const add = useCallback((incoming: readonly File[]) => {
    const created = incoming.map((file) => {
      const id = crypto.randomUUID();
      files.current.set(id, file);
      return { id, name: file.name, sizeBytes: file.size, state: { kind: 'waiting' as const } };
    });

    setItems((current) => [...current, ...created]);
  }, []);

  const retry = useCallback(
    (id: string) => {
      patch(id, { kind: 'waiting' });
    },
    [patch],
  );

  const dismiss = useCallback((id: string) => {
    files.current.delete(id);
    sessions.current.delete(id);
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const active = items.some(
    (item) => item.state.kind === 'waiting' || item.state.kind === 'uploading',
  );

  return { items, add, retry, dismiss, active };
}
